import React, { useState } from 'react';

import CircleIcon from '@mui/icons-material/Circle';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { green, grey, red } from '@mui/material/colors';

import Image from '~/components/Common/Image';
import Spinner from '~/components/Common/Spinner';

import { useDeletePortPOI } from '~/hooks/apis/cruises/useDeletePortPOI';
import { useGetPortPOIs } from '~/hooks/apis/cruises/useGetPortPOIs';

import { Port, PortPOI } from '../types';

interface Props {
  port: Port;
}

const CrawlerStatusColors = {
  crawled: green[500],
  pending: grey[500],
  error: red[500],
};

function PortPOIs({ port }: Props) {
  const { data, isLoading } = useGetPortPOIs(port.id);
  const { mutate: deletePOI, isPending: isDeleting } = useDeletePortPOI();
  const pois = data?.result ?? [];

  const [poiToDelete, setPoiToDelete] = useState<PortPOI | null>(null);

  const crawlerStatus = port.crawlerPOIStatus ?? 'pending';
  const statusColor = CrawlerStatusColors[crawlerStatus as keyof typeof CrawlerStatusColors] ?? grey[500];

  const handleDeleteConfirm = () => {
    if (!poiToDelete) {
      return;
    }
    deletePOI({ portId: port.id, poiId: poiToDelete.id }, { onSuccess: () => setPoiToDelete(null) });
  };

  return (
    <>
      <Stack divider={<Divider orientation="horizontal" flexItem />}>
        <Stack sx={{ bgcolor: 'grey.50' }} p={2} spacing={1}>
          <Typography variant="body1" alignItems="center" display="flex" paddingLeft={1} gap={1}>
            <CircleIcon sx={{ color: statusColor, fontSize: 12 }} />
            <b>Crawler status:</b>
            {crawlerStatus}
          </Typography>
        </Stack>

        <Box p={2}>
          <List>
            {!isLoading &&
              pois.map((poi) => (
                <React.Fragment key={poi.id}>
                  <ListItem
                    sx={{ gap: 2 }}
                    secondaryAction={
                      <IconButton edge="end" aria-label="delete" onClick={() => setPoiToDelete(poi)}>
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <Image publicId={poi.imageId} width={250} />
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          {poi.title}
                          <Chip label={poi.category} size="small" variant="outlined" />
                        </Box>
                      }
                      secondary={poi.description}
                    />
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </React.Fragment>
              ))}

            {!pois.length && !isLoading && (
              <Box display="flex" justifyContent="center">
                <Typography color="textSecondary">No points of interest found</Typography>
              </Box>
            )}

            {isLoading && <Spinner size={24} />}
          </List>
        </Box>
      </Stack>

      <Dialog open={!!poiToDelete} onClose={() => setPoiToDelete(null)}>
        <DialogTitle>Delete point of interest</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <b>{poiToDelete?.title}</b>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setPoiToDelete(null)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={isDeleting}>
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PortPOIs;
